import * as React from "react";
import { CrudPage, CrudResource } from "../components/CrudPage";
import { StatusBadge, Badge } from "../components/Badge";
import { Banner } from "../components/States";
import { IdBadge } from "../components/Detail";
import { useAsync } from "../general/useAsync";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import * as Management from "../management/ManagementStore";
import * as Store from "./EnrollmentStore";

/**
 * Enrollment: which students are in which cohorts, subjects and class groups.
 *
 * Student identity comes from the identity server, not this database, so these screens show a raw
 * user id rather than a name. Resolving names is a separate lookup and is only worth the round trip
 * where a human reads them one at a time - mark entry - not on an administrative list of hundreds.
 */

const useCanWrite = () => usePermission(Permissions.ENROLLMENT);

const statusOptions = Store.ENROLLMENT_STATUSES.map((s) => ({ value: s, label: s }));
const typeOptions = Store.ENROLLMENT_TYPES.map((t) => ({ value: t, label: t }));

const PAGED_NOTE = (
    <Banner tone="info" className="mb-4">
        This list is paged server-side and shows the first 200 rows. The unpaged endpoint would return every enrollment in the
        organisation, so it is deliberately not used here.
    </Banner>
);

// ------------------------------------------------ student module enrollments

interface ModuleEnrollmentDraft {
    studentID: string;
    qualificationModuleID: string;
    enrollmentStatus: string;
}

export const ModuleEnrollmentScreen: React.FC = () => {
    const canWrite = useCanWrite();
    const modules = useAsync(() => Management.listQualificationModules(), [], { initial: [] });

    const resource: CrudResource<Store.StudentModuleEnrollmentSyncModel, ModuleEnrollmentDraft> = {
        title: "Module enrollment",
        icon: ["fad", "user-check"],
        description:
            "Which students are taking which qualification modules. Only Enrolled and Completed students are included in a calculation.",
        singular: "Enrollment",
        plural: "Enrollments",
        list: () => Store.listStudentModuleEnrollments(),
        create: (d) =>
            Store.createStudentModuleEnrollment({
                studentID: d.studentID.trim(),
                qualificationModuleID: d.qualificationModuleID,
                enrollmentStatus: d.enrollmentStatus,
            }),
        // Only the status is updatable - the API will not move an enrollment between modules.
        update: (id, d) => Store.updateStudentModuleEnrollment(id, d.enrollmentStatus),
        remove: Store.deleteStudentModuleEnrollment,
        idOf: (e) => e.id ?? "",
        labelOf: (e) => `${e.moduleName ?? "module"} for ${e.studentID ?? "student"}`,
        searchOf: (e) => `${e.studentID ?? ""} ${e.moduleName ?? ""} ${e.enrollmentStatus ?? ""}`,
        columns: [
            { header: "Student", render: (e) => <IdBadge id={e.studentID} /> },
            { header: "Module", render: (e) => <span className="font-medium text-text-strong">{e.moduleName}</span> },
            { header: "Status", render: (e) => <StatusBadge status={Store.enumerationName(e.enrollmentStatus)} /> },
            {
                header: "Source",
                render: (e) => (e.source ? <Badge tone="neutral">{e.source}</Badge> : <span className="text-text-weak">&mdash;</span>),
            },
        ],
        fields: [
            {
                name: "studentID",
                label: "Student id",
                required: true,
                hint: "The identity-server user id. Students are not stored in this database.",
            },
            {
                name: "qualificationModuleID",
                label: "Qualification module",
                type: "select",
                placeholder: "Select",
                options: (modules.data ?? []).map((m) => ({ value: m.id ?? "", label: Management.qualificationModuleLabel(m) })),
                hint: "Set on create. The update endpoint only changes status.",
            },
            { name: "enrollmentStatus", label: "Status", type: "select", options: statusOptions },
        ],
        toDraft: (e) => ({
            studentID: e?.studentID ?? "",
            qualificationModuleID: e?.qualificationModuleID ?? "",
            enrollmentStatus: Store.enumerationName(e?.enrollmentStatus) || "Enrolled",
        }),
        canWrite,
    };

    return (
        <CrudPage resource={resource} deps={[modules.data]}>
            {PAGED_NOTE}
        </CrudPage>
    );
};

// --------------------------------------------- qualification level enrollments

interface LevelEnrollmentDraft {
    studentID: string;
    qualificationProgramLevelID: string;
    enrollmentStatus: string;
}

export const LevelEnrollmentScreen: React.FC = () => {
    const canWrite = useCanWrite();
    const levels = useAsync(() => Management.listQualificationProgramLevels(), [], { initial: [] });

    const resource: CrudResource<Store.QualificationLevelEnrollmentAPIModel, LevelEnrollmentDraft> = {
        title: "Level enrollment",
        icon: ["fad", "user-graduate"],
        description: "Placement of a student into a cohort - a qualification level, under a programme, in a year.",
        singular: "Enrollment",
        plural: "Enrollments",
        list: Store.listQualificationLevelEnrollments,
        create: (d) =>
            Store.createQualificationLevelEnrollment({
                studentID: d.studentID.trim(),
                qualificationProgramLevelID: d.qualificationProgramLevelID,
                enrollmentStatus: d.enrollmentStatus,
            }),
        update: (id, d) => Store.updateQualificationLevelEnrollment(id, d.enrollmentStatus),
        remove: Store.deleteQualificationLevelEnrollment,
        idOf: (e) => e.id ?? "",
        labelOf: (e) => `${Management.programLevelLabel(e.qualificationProgramLevel)} for ${e.studentID ?? "student"}`,
        searchOf: (e) => `${e.studentID ?? ""} ${Management.programLevelLabel(e.qualificationProgramLevel)}`,
        columns: [
            { header: "Student", render: (e) => <IdBadge id={e.studentID} /> },
            {
                header: "Cohort",
                render: (e) => (
                    <span className="font-medium text-text-strong">{Management.programLevelLabel(e.qualificationProgramLevel) || "—"}</span>
                ),
            },
            { header: "Status", render: (e) => <StatusBadge status={Store.enumerationName(e.enrollmentStatus)} /> },
        ],
        fields: [
            { name: "studentID", label: "Student id", required: true, hint: "The identity-server user id." },
            {
                name: "qualificationProgramLevelID",
                label: "Program level",
                type: "select",
                placeholder: "Select",
                options: (levels.data ?? []).map((l) => ({ value: l.id ?? "", label: Management.programLevelLabel(l) })),
                hint: "Set on create. The update endpoint only changes status.",
            },
            { name: "enrollmentStatus", label: "Status", type: "select", options: statusOptions },
        ],
        toDraft: (e) => ({
            studentID: e?.studentID ?? "",
            qualificationProgramLevelID: e?.qualificationProgramLevelID ?? "",
            enrollmentStatus: Store.enumerationName(e?.enrollmentStatus) || "Enrolled",
        }),
        canWrite,
    };

    return <CrudPage resource={resource} deps={[levels.data]} />;
};

// -------------------------------------------------- teaching group enrollments

interface GroupEnrollmentDraft {
    studentID: string;
    teachingGroupID: string;
    enrollmentType: string;
}

export const TeachingGroupEnrollmentScreen: React.FC = () => {
    const canWrite = useCanWrite();
    const groups = useAsync(() => Management.listTeachingGroups(), [], { initial: [] });

    const resource: CrudResource<Store.TeachingGroupEnrollmentSyncModel, GroupEnrollmentDraft> = {
        title: "Teaching group members",
        icon: ["fad", "users-gear"],
        description: "Students and educators attached to a class group. Used to narrow a mark sheet to one register class.",
        singular: "Member",
        plural: "Members",
        list: () => Store.listTeachingGroupEnrollments(),
        create: (d) =>
            Store.createTeachingGroupEnrollment({
                studentID: d.studentID.trim(),
                teachingGroupID: d.teachingGroupID,
                enrollmentType: d.enrollmentType,
            }),
        // The server has no update action for this - changing a role means remove and re-add.
        update: () => Promise.reject(new Error("Teaching group membership cannot be edited. Remove the member and add them again.")),
        remove: Store.deleteTeachingGroupEnrollment,
        idOf: (e) => e.id ?? "",
        labelOf: (e) => `${e.userID ?? "member"} in ${e.teachingGroupName ?? "group"}`,
        searchOf: (e) => `${e.userID ?? ""} ${e.teachingGroupName ?? ""} ${e.enrollmentType ?? ""}`,
        columns: [
            { header: "Member", render: (e) => <IdBadge id={e.userID} /> },
            { header: "Teaching group", render: (e) => <span className="font-medium text-text-strong">{e.teachingGroupName}</span> },
            {
                header: "Role",
                render: (e) => <Badge tone={Store.enumerationName(e.enrollmentType) === "Educator" ? "info" : "neutral"}>{Store.enumerationName(e.enrollmentType) || "—"}</Badge>,
            },
        ],
        fields: [
            { name: "studentID", label: "User id", required: true, hint: "The identity-server user id of the student or educator." },
            {
                name: "teachingGroupID",
                label: "Teaching group",
                type: "select",
                placeholder: "Select",
                options: (groups.data ?? []).map((g) => ({ value: g.id ?? "", label: g.name ?? "(unnamed)" })),
            },
            { name: "enrollmentType", label: "Role", type: "select", options: typeOptions },
        ],
        toDraft: (e) => ({
            studentID: e?.userID ?? "",
            teachingGroupID: e?.teachingGroupID ?? "",
            enrollmentType: Store.enumerationName(e?.enrollmentType) || "Student",
        }),
        canWrite,
    };

    return (
        <CrudPage resource={resource} deps={[groups.data]}>
            {PAGED_NOTE}
            <Banner tone="warning" className="mb-4">
                Membership cannot be edited in place - the server has no update action for it. To change someone&rsquo;s role, remove them
                and add them again.
            </Banner>
        </CrudPage>
    );
};
